import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CustomIcon } from '../../../UI';
import './Step2VehicleInfo.scss';
import PhotoUploader from '../../PhotoUploader/PhotoUploader';
import { useUserPermissions } from '../../../../hooks/useUserPermissions';

const Step2VehicleInfo = ({ readOnly = false }) => {
  const { register, formState: { errors }, setValue, watch } = useFormContext();
  const { canAddFreight, canAddVehicles } = useUserPermissions();
  
  // Watch type object
  const type = watch('type') || { freight: false, vehicle: false };
  const typeFreight = type.freight || false;
  const typeVehicle = type.vehicle || false;
  
  // Watch arrays from nested structure
  const vehicleShipments = watch('vehicle.shipment') || [];
  const freightShipments = watch('freight.shipment') || [];
  const vehicleImages = watch('vehicle.vehicleImages') || [];
  const freightImages = watch('freight.freightImages') || [];

  // Handle deleted keys tracking
  const handleVehicleDeletedKey = (deletedKey) => {
    const currentDeleted = watch('deletedVehicleImages') || [];
    if (!currentDeleted.includes(deletedKey)) {
      setValue('deletedVehicleImages', [...currentDeleted, deletedKey], { shouldDirty: true });
    }
  };

  const handleFreightDeletedKey = (deletedKey) => {
    const currentDeleted = watch('deletedFreightImages') || [];
    if (!currentDeleted.includes(deletedKey)) {
      setValue('deletedFreightImages', [...currentDeleted, deletedKey], { shouldDirty: true });
    }
  };

  // Determine if fields should be shown based on permissions
  const shouldShowFreightFields = canAddFreight && (typeFreight || (!canAddVehicles && canAddFreight));
  const shouldShowVehicleFields = canAddVehicles && (typeVehicle || (!canAddFreight && canAddVehicles));

  // Auto-check and set default type based on user permissions
  useEffect(() => {
    if (readOnly) return;
    // If user can only add Freight, auto-check it and ensure it stays checked
    if (canAddFreight && !canAddVehicles) {
      setValue('type.freight', true, { shouldDirty: true });
      setValue('type.vehicle', false, { shouldDirty: true });
    }
    // If user can only add Vehicles, auto-check it and ensure it stays checked
    else if (canAddVehicles && !canAddFreight) {
      setValue('type.freight', false, { shouldDirty: true });
      setValue('type.vehicle', true, { shouldDirty: true });
    }
  }, [canAddFreight, canAddVehicles, setValue, readOnly]);

  // Initialize with one empty item if type is selected and array is empty
  useEffect(() => {
    if (readOnly) return;
    if (typeVehicle && vehicleShipments.length === 0) {
      setValue('vehicle.shipment', [{ vin: '', make: '', model: '', year: '', value: '' }], { shouldDirty: true, shouldValidate: true });
    }
    if (typeFreight && freightShipments.length === 0) {
      setValue('freight.shipment', [{ commodity: '', dimensionsLength: '', dimensionsWidth: '', dimensionsHeight: '', dimensionsUnit: 'feet', onPallets: 'no', weight: '', shipmentUnits: 'N/A', poNumber: '', pickupNumber: '', deliveryReference: '' }], { shouldDirty: true, shouldValidate: true });
    }
  }, [typeVehicle, typeFreight, setValue, vehicleShipments.length, freightShipments.length, readOnly]);

  // Add new vehicle shipment
  const addVehicleShipment = () => {
    if (readOnly) return;
    const newShipments = [...vehicleShipments, { vin: '', make: '', model: '', year: '', value: '' }];
    setValue('vehicle.shipment', newShipments, { shouldDirty: true, shouldValidate: true });
  };

  // Remove vehicle shipment
  const removeVehicleShipment = (index) => {
    if (readOnly) return;
    const newShipments = vehicleShipments.filter((_, i) => i !== index);
    setValue('vehicle.shipment', newShipments, { shouldDirty: true, shouldValidate: true });
  };

  // Add new freight shipment
  const addFreightShipment = () => {
    if (readOnly) return;
    const newShipments = [...freightShipments, { commodity: '', dimensionsLength: '', dimensionsWidth: '', dimensionsHeight: '', dimensionsUnit: 'feet', onPallets: 'no', weight: '', shipmentUnits: 'N/A', poNumber: '', pickupNumber: '', deliveryReference: '' }];
    setValue('freight.shipment', newShipments, { shouldDirty: true, shouldValidate: true });
  };

  // Remove freight shipment
  const removeFreightShipment = (index) => {
    if (readOnly) return;
    const newShipments = freightShipments.filter((_, i) => i !== index);
    setValue('freight.shipment', newShipments, { shouldDirty: true, shouldValidate: true });
  };

  // Handle vehicle images change
  const handleVehicleImagesChange = (photos) => {
    if (readOnly) return;
    setValue('vehicle.vehicleImages', photos, { shouldDirty: true, shouldValidate: true });
  };

  // Handle freight images change
  const handleFreightImagesChange = (photos) => {
    if (readOnly) return;
    setValue('freight.freightImages', photos, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <div className="step-2-vehicle-info">
      <div className="shipment-details-section">
        <h3 className="form-section-title">Shipment Details</h3>
        
        <div className="form-grid">
          <div className="form-group full-width">
            <label>Type *</label>
            <div className="type-checkboxes">
              {canAddFreight && (
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="type.freight"
                    {...register('type.freight', {
                      validate: (v) => {
                        if (v) {
                          if (!Array.isArray(freightShipments) || freightShipments.length === 0) {
                            return 'At least one freight shipment is required';
                          }
                          return true;
                        }
                        if (typeVehicle) return true;
                        return 'Select Freight or Vehicles';
                      },
                    })}
                    disabled={!canAddVehicles && canAddFreight}
                  />
                  <label htmlFor="type.freight">Freight</label>
                </div>
              )}
              {canAddVehicles && (
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="type.vehicle"
                    {...register('type.vehicle', {
                      validate: (v) => {
                        if (v) {
                          if (!Array.isArray(vehicleShipments) || vehicleShipments.length === 0) {
                            return 'At least one vehicle shipment is required';
                          }
                          const hasAllVin = vehicleShipments.every((s) => (s?.vin || '').trim() !== '');
                          if (!hasAllVin) return 'VIN is required';
                          return true;
                        }
                        if (typeFreight) return true;
                        return 'Select Freight or Vehicles';
                      },
                    })}
                    disabled={!canAddFreight && canAddVehicles}
                  />
                  <label htmlFor="type.vehicle">Vehicles</label>
                </div>
              )}
            </div>
            </div>
        </div>

        {/* Vehicles Section */}
        {shouldShowVehicleFields && (
          <div className="vehicles-section">
            <div className="section-header">
              <h4 className="section-subtitle">Vehicles</h4>
              <button
                type="button"
                className="add-item-btn"
                onClick={addVehicleShipment}
              >
                <CustomIcon name="plus" size={16} />
                Add Vehicle
              </button>
            </div>

            {vehicleShipments.map((vehicle, index) => (
              <div key={index} className="shipment-item">
                <div className="item-header">
                  <h5 className="item-title">Vehicle {index + 1}</h5>
                  {vehicleShipments.length > 1 && (
                    <button
                      type="button"
                      className="remove-item-btn"
                      onClick={() => removeVehicleShipment(index)}
                    >
                      <CustomIcon name="close" size={14} />
                      Remove
                    </button>
                  )}
                </div>

                <div className="vehicle-form-compact">
                  <div className="vehicle-row">
                    <div className="form-group">
                      <label htmlFor={`vehicle.shipment.${index}.vin`}>
                        VIN *
                      </label>
                      <input
                        type="text"
                        id={`vehicle.shipment.${index}.vin`}
                        placeholder="Text"
                        {...register(`vehicle.shipment.${index}.vin`, {
                          required: typeVehicle ? 'VIN is required' : false
                        })}
                        className={errors.vehicle?.shipment?.[index]?.vin ? 'error' : ''}
                      />
                      {errors.vehicle?.shipment?.[index]?.vin && (
                        <span className="error-message">
                          {errors.vehicle.shipment[index].vin.message}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor={`vehicle.shipment.${index}.make`}>
                        Make
                      </label>
                      <input
                        type="text"
                        id={`vehicle.shipment.${index}.make`}
                        placeholder="Text"
                        {...register(`vehicle.shipment.${index}.make`)}
                        className={errors.vehicle?.shipment?.[index]?.make ? 'error' : ''}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`vehicle.shipment.${index}.model`}>
                        Model
                      </label>
                      <input
                        type="text"
                        id={`vehicle.shipment.${index}.model`}
                        placeholder="Text"
                        {...register(`vehicle.shipment.${index}.model`)}
                        className={errors.vehicle?.shipment?.[index]?.model ? 'error' : ''}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`vehicle.shipment.${index}.year`}>
                        Year
                      </label>
                      <input
                        type="text"
                        id={`vehicle.shipment.${index}.year`}
                        placeholder="Text"
                        {...register(`vehicle.shipment.${index}.year`)}
                        className={errors.vehicle?.shipment?.[index]?.year ? 'error' : ''}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`vehicle.shipment.${index}.value`}>
                        Value ($)
                      </label>
                      <input
                        type="text"
                        id={`vehicle.shipment.${index}.value`}
                        placeholder="Text"
                        {...register(`vehicle.shipment.${index}.value`)}
                        className={errors.vehicle?.shipment?.[index]?.value ? 'error' : ''}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <PhotoUploader
              title="Vehicle Images"
              photos={vehicleImages}
              onPhotosChange={handleVehicleImagesChange}
              onDeletedKeysChange={handleVehicleDeletedKey}
              maxFileSize={120}
              acceptedTypes="image/png,image/jpeg,image/jpg"
              showCount={true}
            />
          </div>
        )}

        {/* Freight Section */}
        {shouldShowFreightFields && (
          <div className="freight-section">
            <div className="section-header">
              <h4 className="section-subtitle">Freight</h4>
              <button
                type="button"
                className="add-item-btn"
                onClick={addFreightShipment}
              >
                <CustomIcon name="plus" size={16} />
                Add Freight
              </button>
            </div>

            {freightShipments.map((freight, index) => (
              <div key={index} className="shipment-item">
                <div className="item-header">
                  <h5 className="item-title">Freight {index + 1}</h5>
                  {freightShipments.length > 1 && (
                    <button
                      type="button"
                      className="remove-item-btn"
                      onClick={() => removeFreightShipment(index)}
                    >
                      <CustomIcon name="close" size={14} />
                      Remove
                    </button>
                  )}
                </div>

                <div className="freight-form-compact">
                  {/* First row: Commodity, Dimensions, Weight */}
                  <div className="freight-row freight-row-main">
                    <div className="form-group">
                      <label htmlFor={`freight.shipment.${index}.commodity`}>
                        Commodity
                      </label>
                      <input
                        type="text"
                        id={`freight.shipment.${index}.commodity`}
                        placeholder="Text"
                        {...register(`freight.shipment.${index}.commodity`)}
                        className={errors.freight?.shipment?.[index]?.commodity ? 'error' : ''}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`freight.shipment.${index}.dimensionsUnit`}>
                        Unit
                      </label>
                      <select
                        id={`freight.shipment.${index}.dimensionsUnit`}
                        {...register(`freight.shipment.${index}.dimensionsUnit`)}
                        className={errors.freight?.shipment?.[index]?.dimensionsUnit ? 'error' : ''}
                      >
                        <option value="feet">Feet</option>
                        <option value="inches">Inches</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor={`freight.shipment.${index}.dimensionsLength`}>
                        Length
                      </label>
                      <input
                        type="text"
                        id={`freight.shipment.${index}.dimensionsLength`}
                        placeholder="Number or N/A"
                        {...register(`freight.shipment.${index}.dimensionsLength`)}
                        className={errors.freight?.shipment?.[index]?.dimensionsLength ? 'error' : ''}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`freight.shipment.${index}.dimensionsWidth`}>
                        Width
                      </label>
                      <input
                        type="text"
                        id={`freight.shipment.${index}.dimensionsWidth`}
                        placeholder="Number or N/A"
                        {...register(`freight.shipment.${index}.dimensionsWidth`)}
                        className={errors.freight?.shipment?.[index]?.dimensionsWidth ? 'error' : ''}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`freight.shipment.${index}.dimensionsHeight`}>
                        Height
                      </label>
                      <input
                        type="text"
                        id={`freight.shipment.${index}.dimensionsHeight`}
                        placeholder="Number or N/A"
                        {...register(`freight.shipment.${index}.dimensionsHeight`)}
                        className={errors.freight?.shipment?.[index]?.dimensionsHeight ? 'error' : ''}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`freight.shipment.${index}.onPallets`}>
                        On pallets
                      </label>
                      <select
                        id={`freight.shipment.${index}.onPallets`}
                        {...register(`freight.shipment.${index}.onPallets`)}
                        className={errors.freight?.shipment?.[index]?.onPallets ? 'error' : ''}
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor={`freight.shipment.${index}.weight`}>
                        Weight (lbs)
                      </label>
                      <input
                        type="text"
                        id={`freight.shipment.${index}.weight`}
                        placeholder="Weight"
                        {...register(`freight.shipment.${index}.weight`)}
                        className={errors.freight?.shipment?.[index]?.weight ? 'error' : ''}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`freight.shipment.${index}.shipmentUnits`}>
                        Shipment units
                      </label>
                      <select
                        id={`freight.shipment.${index}.shipmentUnits`}
                        {...register(`freight.shipment.${index}.shipmentUnits`)}
                        className={errors.freight?.shipment?.[index]?.shipmentUnits ? 'error' : ''}
                      >
                        <option value="N/A">N/A</option>
                        {Array.from({ length: 200 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={String(n)}>{n}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Second row: PO#, Pickup #, Delivery Reference # */}

                  <div className="freight-row freight-row-optional">
                    <div className="form-group">
                      <label htmlFor={`freight.shipment.${index}.poNumber`}>
                        PO# (Optional)
                      </label>
                      <input
                        type="text"
                        id={`freight.shipment.${index}.poNumber`}
                        placeholder="PO Number"
                        {...register(`freight.shipment.${index}.poNumber`)}
                        className={errors.freight?.shipment?.[index]?.poNumber ? 'error' : ''}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`freight.shipment.${index}.pickupNumber`}>
                        Pickup # (Optional)
                      </label>
                      <input
                        type="text"
                        id={`freight.shipment.${index}.pickupNumber`}
                        placeholder="Pickup Number"
                        {...register(`freight.shipment.${index}.pickupNumber`)}
                        className={errors.freight?.shipment?.[index]?.pickupNumber ? 'error' : ''}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`freight.shipment.${index}.deliveryReference`}>
                        Delivery Reference # (Optional)
                      </label>
                      <input
                        type="text"
                        id={`freight.shipment.${index}.deliveryReference`}
                        placeholder="Delivery Reference"
                        {...register(`freight.shipment.${index}.deliveryReference`)}
                        className={errors.freight?.shipment?.[index]?.deliveryReference ? 'error' : ''}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <PhotoUploader
              title="Freight Images"
              photos={freightImages}
              onPhotosChange={handleFreightImagesChange}
              onDeletedKeysChange={handleFreightDeletedKey}
              maxFileSize={120}
              acceptedTypes="image/png,image/jpeg,image/jpg"
              showCount={true}
            />
          </div>
        )}

        {(shouldShowFreightFields || shouldShowVehicleFields) && (
          <div className="form-group full-width special-requirements-common">
            <label htmlFor="vehicle.specialRequirements">
              Special Requirements
            </label>
            <textarea
              id="vehicle.specialRequirements"
              placeholder="Text"
              rows={4}
              {...register('vehicle.specialRequirements')}
              className={errors.vehicle?.specialRequirements ? 'error' : ''}
            />
            {errors.vehicle?.specialRequirements && (
              <span className="error-message">{errors.vehicle.specialRequirements.message}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Step2VehicleInfo;